import { useCallback, useState } from "react";
import InfoHover from "../../../components/ui/InfoHover";
import Input from "../../../components/ui/Input";
import { Button } from "../../../components/ui/buttons/Button";
import { noop } from "../../../utils/noop";
import { twMerge } from "tailwind-merge";
import { currencyList } from "../../../components/client-only/RecommendedHiringForm";

const FundItem = ({
    onClick = noop,
    title = "Tarjeta de débito o credito",
    desc = null,
    disabled = false,
    active = false,
    selected = "",
    icon = (
        <svg
            width={40}
            height={40}
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="relative w-10 h-10"
            preserveAspectRatio="xMidYMid meet"
        >
            <path
                d="M32.4479 8.3335H7.5C6.09375 8.3335 5 9.47933 5 10.8335V29.1668C5 30.5731 6.09375 31.6668 7.5 31.6668H32.4479C33.8542 31.6668 35 30.5731 35 29.1668V10.8335C35 9.47933 33.8542 8.3335 32.4479 8.3335ZM7.8125 10.8335H32.1354C32.3438 10.8335 32.4479 10.9897 32.4479 11.146V13.3335H7.5V11.146C7.5 10.9897 7.60417 10.8335 7.8125 10.8335ZM32.1354 29.1668H7.8125C7.60417 29.1668 7.5 29.0627 7.5 28.8543V20.0002H32.4479V28.8543C32.4479 29.0627 32.3438 29.1668 32.1354 29.1668ZM15 23.9585C15 23.646 14.6875 23.3335 14.375 23.3335H10.625C10.2604 23.3335 10 23.646 10 23.9585V26.0418C10 26.4064 10.2604 26.6668 10.625 26.6668H14.375C14.6875 26.6668 15 26.4064 15 26.0418V23.9585ZM25 23.9585C25 23.646 24.6875 23.3335 24.375 23.3335H17.2917C16.9271 23.3335 16.6667 23.646 16.6667 23.9585V26.0418C16.6667 26.4064 16.9271 26.6668 17.2917 26.6668H24.375C24.6875 26.6668 25 26.4064 25 26.0418V23.9585Z"
                fill="#333333"
            />
        </svg>
    ),
}) => {
    const [cardData, setCardData] = useState({
        cardName: "",
        cardNumber: "",
        cardCvv: "",
        cardExpiry: "",
    });

    const [validationErrors, setValidationErrors] = useState({
        cardNumber: "",
        cardName: "",
        cardCvv: "",
        cardExpiry: "",
    });

    const validateCardNumber = () => {
        const errors = {};
        if (cardData.cardNumber.length !== 16) {
            errors.cardNumber = "El número de tarjeta debe tener 16 dígitos.";
        } else {
            errors.cardNumber = "";
        }
        setValidationErrors((prevErrors) => ({
            ...prevErrors,
            ...errors,
        }));
    };

    const validateCardName = () => {
        const errors = {};
        if (cardData.cardName.trim() === "") {
            errors.cardName = "Ingrese el nombre del titular de la tarjeta.";
        } else {
            errors.cardName = "";
        }
        setValidationErrors((prevErrors) => ({
            ...prevErrors,
            ...errors,
        }));
    };

    const validateCardCvv = () => {
        const errors = {};
        if (cardData.cardCvv.length !== 3) {
            errors.cardCvv = "El CVV debe tener 3 dígitos.";
        } else {
            errors.cardCvv = "";
        }
        setValidationErrors((prevErrors) => ({
            ...prevErrors,
            ...errors,
        }));
    };

    const validateCardExpiry = () => {
        const errors = {};
        const [month, year] = cardData.cardExpiry.split("/");
        const currentDate = new Date();
        const expiryDate = new Date(`20${year}`, month - 1);

        if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(cardData.cardExpiry)) {
            errors.cardExpiry =
                "Ingrese una fecha válida (MM/YY).";
        } else if (expiryDate <= currentDate) {
            errors.cardExpiry = "Seleccione una fecha futura.";
        } else {
            errors.cardExpiry = "";
        }
        setValidationErrors((prevErrors) => ({
            ...prevErrors,
            ...errors,
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
    
        let maxLength = null;
        if (name === "cardNumber") {
            maxLength = 16; 
        } else if (name === "cardExpiry") {
            maxLength = 5; 
        } else if (name === "cardCvv") {
            maxLength = 3; 
        }
    
        const truncatedValue = maxLength ? value.slice(0, maxLength) : value;
    
        setCardData({
            ...cardData,
            [name]: truncatedValue,
        });
    };

    const handleAddCard = () => {
        validateCardNumber();
        validateCardName();
        validateCardCvv();
        validateCardExpiry();

        // Check if there are any validation errors
        if (Object.values(validationErrors).some((error) => error !== "")) {
            return; // Exit if there are validation errors
        }

        // Proceed with adding the card
        console.log("Card data:", cardData);
        // Reset form fields
        setCardData({
            cardName: "",
            cardNumber: "",
            cardCvv: "",
            cardExpiry: "",
        });
    };

    return (
        <div className="w-full">
            <div
                onClick={!disabled ? onClick : undefined} // Only add 'onClick' if not disabled
                className={`flex justify-start hover:bg-slate-200 cursor-pointer items-center self-stretch h-[60px] relative px-4 ${
                    active ? "bg-[#f2f2f2]" : "bg-white"
                } border border-[#cdcdcd] ${
                    disabled ? "opacity-50 pointer-events-none" : ""
                }`}
            >
                <div className="  flex justify-start items-center flex-grow relative gap-2.5">
                    {active ? (
                        <svg
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="relative w-6 h-6"
                            preserveAspectRatio="none"
                        >
                            <path
                                d="M12 5.75C15.4375 5.75 18.25 8.5625 18.25 12C18.25 15.4688 15.4375 18.25 12 18.25C8.53125 18.25 5.75 15.4688 5.75 12C5.75 8.5625 8.53125 5.75 12 5.75ZM12 4.25C7.71875 4.25 4.25 7.75 4.25 12C4.25 16.2812 7.71875 19.75 12 19.75C16.25 19.75 19.75 16.2812 19.75 12C19.75 7.75 16.25 4.25 12 4.25ZM12 9.5C10.5938 9.5 9.5 10.625 9.5 12C9.5 13.4062 10.5938 14.5 12 14.5C13.375 14.5 14.5 13.4062 14.5 12C14.5 10.625 13.375 9.5 12 9.5Z"
                                fill="#D80027"
                            />
                        </svg>
                    ) : (
                        <svg
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="relative w-6 h-6"
                            preserveAspectRatio="none"
                        >
                            <path
                                d="M12 4.25C7.71875 4.25 4.25 7.71875 4.25 12C4.25 16.2812 7.71875 19.75 12 19.75C16.2812 19.75 19.75 16.2812 19.75 12C19.75 7.71875 16.2812 4.25 12 4.25ZM12 18.25C8.53125 18.25 5.75 15.4688 5.75 12C5.75 8.5625 8.53125 5.75 12 5.75C15.4375 5.75 18.25 8.5625 18.25 12C18.25 15.4688 15.4375 18.25 12 18.25Z"
                                fill="#292929"
                            />
                        </svg>
                    )}
                    <div className="flex flex-col justify-center items-start  relative lg:gap-[3px]">
                        <p className=" text-xs sm:text-sm font-bold text-left text-[#373737] ">
                            {title}
                        </p>
                        <p className="text-xs text-left text-[#373737]">
                            {desc}
                        </p>
                    </div>
                </div>
                {icon}
            </div>
            {selected && selected === "CREDIT_NEW" && (
                <div
                    className={twMerge(
                        "w-full overflow-hidden border-x border-[#cdcdcd]"
                    )}
                >
                    <div className="p-3  space-y-5">
                        <p className="bg-yellow-100 p-3 text-xs text-[#373737]">
                            Usted esta agregando una forma de pago como
                            invitado, lo invitamos a registrarse para disfrutar
                            de más beneficios.
                        </p>
                        <div className="flex max-w-full justify-between">
                            <div className="w-6/12 ">
                                <Input
                                    label="Número de tarjeta:"
                                    placeholder="56 5678 867"
                                    onChange={handleInputChange}
                                    value={cardData.cardNumber}
                                    name="cardNumber"
                                    type="number"
                                    className={""}
                                    onBlur={validateCardNumber}
                                />
                                {validationErrors.cardName && (
                                    <p className="text-xs text-red-500">
                                        {validationErrors.cardNumber}
                                    </p>
                                )}
                            </div>
                            <div className="w-5/12 h-10 ">
                                <Input
                                    label="Fecha de vencimiento:"
                                    placeholder="MM / YY"
                                    onChange={handleInputChange}
                                    value={cardData.cardExpiry}
                                    name="cardExpiry"
                                    type="text"
                                    className={""}
                                    onBlur={validateCardExpiry}
                                />
                                {validationErrors.cardExpiry && (
                                    <p className="text-xs text-red-500">
                                        {validationErrors.cardExpiry}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex max-w-full justify-between">
                            <div className="w-6/12 ">
                                <Input
                                    label="Nombre del titular de la tarjeta:"
                                    placeholder="Nombre completo"
                                    onChange={handleInputChange}
                                    value={cardData.cardName}
                                    name="cardName"
                                    type="text"
                                    className={""}
                                    onBlur={validateCardName}
                                />
                                {validationErrors.cardName && (
                                    <p className="text-xs text-red-500">
                                        {validationErrors.cardName}
                                    </p>
                                )}
                            </div>
                            <div className="w-5/12 h-10 ">
                                <Input
                                    label="CVC/CVV:"
                                    placeholder="***"
                                    onChange={handleInputChange}
                                    value={cardData.cardCvv}
                                    name="cardCvv"
                                    type="number"
                                    className={""}
                                    onBlur={validateCardCvv}
                                />
                                {validationErrors.cardCvv && (
                                    <p className="text-xs text-red-500">
                                        {validationErrors.cardCvv}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <Button onClick={handleAddCard}>Agregar</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function Funds({ className, callback }) {
    const [formData, setFormData] = useState({});
    const PAYMENT_OPTIONS = {
        CREDIT_SELECT: "CREDIT_SELECT",
        BANK_SELECT: "BANK_SELECT",
        CREDIT_NEW: "CREDIT_NEW",
        BANK_NEW: "BANK_NEW",
        CRYPTO_NEW: "CRYPTO_NEW",
        OTHERS: "OTHERS",
        CASH_NEW: "CASH_NEW",
        CARD_01: "CARD_01",
    };
    const savedCards = {
        CARD_01: {
            number: 7986987687594444,
            name: "John Doe",
            expiry: "12/25",
            cvv: 123,
        },
    };
    const [selected, setSelected] = useState(PAYMENT_OPTIONS.CREDIT_SELECT);
    const [expand, setExpand] = useState(false);
    const handleChange = useCallback(
        (target = {}) => {
            let { name, value } = target;
            if (value.length > 15) return;
            console.log(target);
            setFormData((data) => ({ ...data, ...{ [name]: value } }));
            console.log(formData);
        },
        [formData]
    );

    const currencyCode = currencyList.find(country => country.name === formData?.currency)?.currencyCode;

    const handleFormSubmission = useCallback(
        (e) => {
            // submit formData
            e.preventDefault();
            // const url = "http://localhost:3000/uploadFile";
            // const config = {
            //     headers: {
            //         "content-type": "multipart/form-data"
            //     }
            // };
            // axios
            //     .post(url, formData, config)
            //     .then((response) => {
            //         console.log(response.data);
            //         setUploadedFile(response.data.file);
            //     })
            //     .catch((error) => {
            //         console.error("Error uploading file: ", error);
            //         setError(error);
            //     });
            callback(formData);
        },
        [callback, formData]
    );

    return (
        <div
            className={
                className ??
                "flex relative flex-col gap-5 justify-center items-center w-full mt-[100px]"
            }
        >
            <div className="box-border flex flex-col p-3 w-full max-w-[936px]   gap-[5px]">
                <p className="whitespace-pre-wrap w-full max-w-[936px] font-['Noto_Sans'] text-base leading-[normal] font-bold text-left text-[#373737]">
                    ¿Cómo deseas pagar?
                </p>
                <p className="whitespace-pre-wrap w-full max-w-[936px] font-['Noto_Sans'] text-base leading-[normal] text-left text-[#373737]">
                    Selecciona tu forma de pago preferida para poder continuar
                </p>
            </div>
            <div className="flex flex-col lg:flex-row   w-full max-w-[936px]  gap-[25px] ">
                <div className="flex flex-col  w-full    gap-[46px]">
                    <div className="flex flex-col justify-start items-start ">
                        <div className="flex justify-between items-center w-full  relative lg:gap-[133px] pl-4 border border-[#cdcdcd]">
                            <p className="flex-grow w-full  text-base font-bold text-left text-[#373737]">
                                Seleccionar forma de pago
                            </p>
                            <div className="flex justify-start items-start  relative gap-2.5 p-2.5 bg-[#292929]">
                                <svg
                                    width={40}
                                    height={40}
                                    viewBox="0 0 40 40"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="relative w-10 h-10"
                                    preserveAspectRatio="xMidYMid meet"
                                >
                                    <path
                                        d="M33.6979 22.9165C32.0833 21.5623 30.1042 22.0832 29.1146 22.8644L25.9896 25.2082H25.7813C25.7813 23.229 24.2188 21.6665 22.1875 21.6665H14.6875C13.2292 21.6665 11.7708 22.1873 10.625 23.0728L9.11458 24.1665H5.83333C5.36458 24.1665 5 24.5832 5 24.9998V25.8332C5 26.3019 5.36458 26.6665 5.83333 26.6665H10L12.1354 25.0519C12.8646 24.479 13.75 24.1665 14.6875 24.1665H22.1875C23.6458 24.1665 23.6979 26.3019 22.1354 26.3019H19.0104C18.6458 26.3019 18.3333 26.6144 18.3333 26.979V27.0311C18.3333 27.3957 18.6458 27.7082 19.0104 27.7082H26.0417C26.5625 27.7082 27.0313 27.5519 27.4479 27.2394L30.625 24.8436C31.0417 24.5311 31.7188 24.5311 32.0833 24.8436C32.6563 25.3123 32.6042 26.0415 32.0833 26.4582L26.8229 30.3644C26.4063 30.6769 25.9375 30.8332 25.4167 30.8332H5.83333C5.36458 30.8332 5 31.2498 5 31.6665V32.4998C5 32.9686 5.36458 33.3332 5.83333 33.3332H25.4167C26.4583 33.3332 27.5 33.0207 28.3333 32.3957L33.5417 28.4373C34.4271 27.8123 34.9479 26.8228 34.9479 25.729C35 24.6873 34.5313 23.6457 33.6979 22.9165ZM18.3854 14.2186L20.9896 14.9478C21.1979 14.9998 21.3021 15.1561 21.3021 15.3644C21.3021 15.6248 21.1458 15.8332 20.8854 15.8332H19.1667C19.0104 15.8332 18.8021 15.7811 18.6458 15.6769C18.3854 15.5728 18.0729 15.6248 17.9167 15.7811L16.9792 16.7186C16.7188 16.979 16.7708 17.4478 17.0833 17.6561C17.5521 18.0207 18.125 18.1769 18.75 18.2811V19.1665C18.75 19.6353 19.1146 19.9998 19.5833 19.9998H20.4167C20.8333 19.9998 21.25 19.6353 21.25 19.1665V18.2811C22.8125 18.0728 24.0104 16.6665 23.8021 14.9998C23.6458 13.8019 22.7083 12.8644 21.5625 12.4998L18.9583 11.7707C18.75 11.7186 18.6458 11.5103 18.6458 11.354C18.6458 11.0936 18.8542 10.8853 19.0625 10.8853H20.7813C20.9896 10.8853 21.1458 10.9373 21.3021 11.0415C21.5625 11.1457 21.875 11.0936 22.0312 10.9373L22.9688 9.99984C23.2292 9.73942 23.2292 9.27067 22.8646 9.06234C22.3958 8.69775 21.8229 8.5415 21.25 8.43734V7.49984C21.25 7.08317 20.8333 6.6665 20.4167 6.6665H19.5833C19.1146 6.6665 18.75 7.08317 18.75 7.49984V8.43734C17.1354 8.64567 15.9375 10.0519 16.1458 11.7186C16.3021 12.9165 17.2396 13.854 18.3854 14.2186Z"
                                        fill="#F2F2F2"
                                    />
                                </svg>
                            </div>
                        </div>

                        <FundItem
                            active={selected === PAYMENT_OPTIONS.CARD_01}
                            onClick={() => setSelected(PAYMENT_OPTIONS.CARD_01)}
                            desc="Se aceptan todas las principales tarjetas"
                        />
                        <FundItem
                            title="Nombre de la tarjeta registrada"
                            desc={`${String(savedCards.CARD_01.number).slice(
                                0,
                                12
                            )}*****`}
                            active={selected === PAYMENT_OPTIONS.CREDIT_SELECT}
                            onClick={() =>
                                setSelected(PAYMENT_OPTIONS.CREDIT_SELECT)
                            }
                        />
                        <FundItem
                            title="Depósito bancario"
                            icon={
                                <svg
                                    width={40}
                                    height={40}
                                    viewBox="0 0 40 40"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="relative w-10 h-10"
                                    preserveAspectRatio="xMidYMid meet"
                                >
                                    <path
                                        d="M31.25 29.5833H30.8333V26.6667C30.8333 25.9896 30.2604 25.4167 29.5833 25.4167H28.75V17.5H26.25V25.4167H23.75V17.5H21.25V25.4167H18.75V17.5H16.25V25.4167H13.75V17.5H11.25V25.4167H10.4167C9.6875 25.4167 9.16667 25.9896 9.16667 26.6667V29.5833H8.75C8.02083 29.5833 7.5 30.1562 7.5 30.8333V31.6667C7.5 31.9271 7.65625 32.0833 7.91667 32.0833H32.0833C32.2917 32.0833 32.5 31.9271 32.5 31.6667V30.8333C32.5 30.1562 31.9271 29.5833 31.25 29.5833ZM28.3333 29.5833H11.6667V27.9167H28.3333V29.5833ZM32.0833 12.8125L20.7812 8.07292C20.5208 7.96875 20.2604 7.96875 20 7.96875C19.6875 7.96875 19.4271 7.96875 19.1667 8.07292L7.86458 12.8125C7.65625 12.8646 7.5 13.125 7.5 13.3854V14.7917C7.5 15.1562 7.76042 15.4167 8.125 15.4167H9.16667V16.0417C9.16667 16.4062 9.42708 16.6667 9.79167 16.6667H30.2083C30.5208 16.6667 30.8333 16.4062 30.8333 16.0417V15.4167H31.875C32.1875 15.4167 32.5 15.1562 32.5 14.7917V13.3854C32.5 13.125 32.2917 12.8646 32.0833 12.8125ZM10 14.1667L20 10.4167L30 14.1667H10Z"
                                        fill="#333333"
                                    />
                                </svg>
                            }
                            active={selected === PAYMENT_OPTIONS.BANK_SELECT}
                            onClick={() =>
                                setSelected(PAYMENT_OPTIONS.BANK_SELECT)
                            }
                        />
                    </div>
                    <div className="flex flex-col justify-start items-start self-stretch">
                        <div className="flex justify-start items-center self-stretch  h-[60px] relative pl-4 border border-[#cdcdcd]">
                            <p className="flex-grow w-full max-w-[439px] text-base font-bold text-left text-[#373737]">
                                Agregar nueva forma de pago
                            </p>
                        </div>

                        <FundItem
                            title="Tarjeta de débito o credito"
                            desc="Se aceptan todas las principales tarjetas"
                            icon={
                                <svg
                                    width={40}
                                    height={40}
                                    viewBox="0 0 40 40"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="relative w-10 h-10"
                                    preserveAspectRatio="xMidYMid meet"
                                >
                                    <path
                                        d="M32.4479 8.3335H7.5C6.09375 8.3335 5 9.47933 5 10.8335V29.1668C5 30.5731 6.09375 31.6668 7.5 31.6668H32.4479C33.8542 31.6668 35 30.5731 35 29.1668V10.8335C35 9.47933 33.8542 8.3335 32.4479 8.3335ZM7.8125 10.8335H32.1354C32.3438 10.8335 32.4479 10.9897 32.4479 11.146V13.3335H7.5V11.146C7.5 10.9897 7.60417 10.8335 7.8125 10.8335ZM32.1354 29.1668H7.8125C7.60417 29.1668 7.5 29.0627 7.5 28.8543V20.0002H32.4479V28.8543C32.4479 29.0627 32.3438 29.1668 32.1354 29.1668ZM15 23.9585C15 23.646 14.6875 23.3335 14.375 23.3335H10.625C10.2604 23.3335 10 23.646 10 23.9585V26.0418C10 26.4064 10.2604 26.6668 10.625 26.6668H14.375C14.6875 26.6668 15 26.4064 15 26.0418V23.9585ZM25 23.9585C25 23.646 24.6875 23.3335 24.375 23.3335H17.2917C16.9271 23.3335 16.6667 23.646 16.6667 23.9585V26.0418C16.6667 26.4064 16.9271 26.6668 17.2917 26.6668H24.375C24.6875 26.6668 25 26.4064 25 26.0418V23.9585Z"
                                        fill="#333333"
                                    />
                                </svg>
                            }
                            active={selected === PAYMENT_OPTIONS.CREDIT_NEW}
                            selected={selected}
                            onClick={() =>
                                setSelected(PAYMENT_OPTIONS.CREDIT_NEW)
                            }
                        />
                        <FundItem
                            title="Depósito bancario"
                            icon={
                                <svg
                                    width={40}
                                    height={40}
                                    viewBox="0 0 40 40"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="relative w-10 h-10"
                                    preserveAspectRatio="xMidYMid meet"
                                >
                                    <path
                                        d="M31.25 29.5833H30.8333V26.6667C30.8333 25.9896 30.2604 25.4167 29.5833 25.4167H28.75V17.5H26.25V25.4167H23.75V17.5H21.25V25.4167H18.75V17.5H16.25V25.4167H13.75V17.5H11.25V25.4167H10.4167C9.6875 25.4167 9.16667 25.9896 9.16667 26.6667V29.5833H8.75C8.02083 29.5833 7.5 30.1562 7.5 30.8333V31.6667C7.5 31.9271 7.65625 32.0833 7.91667 32.0833H32.0833C32.2917 32.0833 32.5 31.9271 32.5 31.6667V30.8333C32.5 30.1562 31.9271 29.5833 31.25 29.5833ZM28.3333 29.5833H11.6667V27.9167H28.3333V29.5833ZM32.0833 12.8125L20.7812 8.07292C20.5208 7.96875 20.2604 7.96875 20 7.96875C19.6875 7.96875 19.4271 7.96875 19.1667 8.07292L7.86458 12.8125C7.65625 12.8646 7.5 13.125 7.5 13.3854V14.7917C7.5 15.1562 7.76042 15.4167 8.125 15.4167H9.16667V16.0417C9.16667 16.4062 9.42708 16.6667 9.79167 16.6667H30.2083C30.5208 16.6667 30.8333 16.4062 30.8333 16.0417V15.4167H31.875C32.1875 15.4167 32.5 15.1562 32.5 14.7917V13.3854C32.5 13.125 32.2917 12.8646 32.0833 12.8125ZM10 14.1667L20 10.4167L30 14.1667H10Z"
                                        fill="#333333"
                                    />
                                </svg>
                            }
                            active={selected === PAYMENT_OPTIONS.BANK_NEW}
                            onClick={() =>
                                setSelected(PAYMENT_OPTIONS.BANK_NEW)
                            }
                        />

                        <FundItem
                            disabled
                            title="Criptomonedas"
                            icon={
                                <svg
                                    width={40}
                                    height={40}
                                    viewBox="0 0 40 40"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="relative w-10 h-10"
                                    preserveAspectRatio="xMidYMid meet"
                                >
                                    <path
                                        d="M26.1458 19.3228C27.5521 18.5936 28.4896 17.2915 28.2812 15.104C28.0208 12.0832 25.5729 11.0936 22.2917 10.8332V6.6665H19.7917V10.729C19.1146 10.729 18.4375 10.729 17.7604 10.729V6.6665H15.2604V10.8332C14.3229 10.8332 13.2292 10.8332 10.1562 10.8332V13.5415C12.1875 13.4894 13.2292 13.3332 13.4375 14.6353V25.9373C13.2813 26.979 12.5 26.8228 10.6771 26.8228L10.1562 29.7915C14.7917 29.7915 15.2604 29.7915 15.2604 29.7915V33.3332H17.7604V29.8436C18.4375 29.8957 19.1146 29.8957 19.7917 29.8957V33.3332H22.2917V29.7915C26.5104 29.5832 29.375 28.5415 29.7396 24.5311C30.0521 21.354 28.5417 19.8957 26.1458 19.3228ZM17.8125 13.6978C19.2708 13.6978 23.6979 13.2811 23.6979 16.2498C23.6979 19.0623 19.2708 18.7498 17.8125 18.7498V13.6978ZM17.8125 26.8228V21.2498C19.5312 21.2498 24.7396 20.7811 24.7396 24.0623C24.7396 27.1873 19.5312 26.8228 17.8125 26.8228Z"
                                        fill="#333333"
                                    />
                                </svg>
                            }
                            active={selected === PAYMENT_OPTIONS.CRYPTO_NEW}
                            onClick={() =>
                                setSelected(PAYMENT_OPTIONS.CRYPTO_NEW)
                            }
                        />
                        <FundItem
                            disabled
                            title="Efectivo"
                            icon={
                                <svg
                                    width={40}
                                    height={40}
                                    viewBox="0 0 40 40"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="relative w-10 h-10"
                                    preserveAspectRatio="xMidYMid meet"
                                >
                                    <path
                                        d="M35.0007 10H5.00065C4.06315 10 3.33398 10.7812 3.33398 11.6667V28.3333C3.33398 29.2708 4.06315 30 5.00065 30H35.0007C35.8861 30 36.6673 29.2708 36.6673 28.3333V11.6667C36.6673 10.7812 35.8861 10 35.0007 10ZM34.1673 24.1667C32.2923 24.1667 30.834 25.6771 30.834 27.5H9.16732C9.16732 25.6771 7.6569 24.1667 5.83398 24.1667V15.8333C7.6569 15.8333 9.16732 14.375 9.16732 12.5H30.834C30.834 14.375 32.2923 15.8333 34.1673 15.8333V24.1667ZM20.0007 15C17.6569 15 15.834 17.2396 15.834 20C15.834 22.8125 17.6569 25 20.0007 25C22.2923 25 24.1673 22.8125 24.1673 20C24.1673 17.2396 22.2923 15 20.0007 15Z"
                                        fill="#333333"
                                    />
                                </svg>
                            }
                            active={selected === PAYMENT_OPTIONS.CASH_NEW}
                            onClick={() =>
                                setSelected(PAYMENT_OPTIONS.CASH_NEW)
                            }
                        />
                        <FundItem
                            title="Mostrar todas las opciones de pago"
                            icon={
                                <svg
                                    width={24}
                                    height={24}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`relative w-6 h-6 ${
                                        expand && "rotate-180"
                                    } `}
                                    preserveAspectRatio="xMidYMid meet"
                                >
                                    <path
                                        d="M18.7812 9.25L18.1875 8.625C18.0312 8.46875 17.7812 8.46875 17.6562 8.625L12 14.2812L6.3125 8.625C6.1875 8.46875 5.9375 8.46875 5.78125 8.625L5.1875 9.25C5.03125 9.375 5.03125 9.625 5.1875 9.78125L11.7188 16.3125C11.875 16.4688 12.0938 16.4688 12.25 16.3125L18.7812 9.78125C18.9375 9.625 18.9375 9.375 18.7812 9.25Z"
                                        fill="#333333"
                                    />
                                </svg>
                            }
                            active={selected === PAYMENT_OPTIONS.OTHERS}
                            onClick={() => setExpand((e) => !e)}
                        />
                    </div>
                </div>
                <div className="flex flex-col  min-w-[300px] ">
                    <div className="flex w-full    h-[60px] relative  pl-4 border border-[#cdcdcd]">
                        <p className="flex flex-row h-full items-center flex-grow w-full  text-base font-bold text-left text-[#373737]">
                            Seleccionar forma de pago
                        </p>
                        <div className="flex justify-center items-center   w-full max-w-[60px] relative gap-2.5 p-2.5 bg-[#292929]">
                            <p className="text-base font-bold text-left text-white">
                                GTQ
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col justify-center items-start self-stretch  gap-5 px-4 py-5 border border-[#cdcdcd]">
                        <div className="flex relative flex-col gap-5 justify-start self-stretch lg:items-center lg:flex-row">
                            <p className="flex-grow w-full max-w-[196px] text-sm font-bold text-left text-[#373737] ">
                                Moneda
                            </p>
                            <Input
                                name="currency"
                                value={formData.currency}
                                onChange={(e) => handleChange(e.target)}
                                type="dropdown"
                                placeholder="🇨🇷 Quetzales"
                                selectList={currencyList.map(country => country.name)}
                                addInputClass="pr-8"
                                className={"flex relative flex-col min-w-max gap-[5px]"}
                                icon={
                                    <svg
                                        width={24}
                                        height={24}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="relative w-6 h-6"
                                        preserveAspectRatio="xMidYMid meet"
                                    >
                                        <path
                                            d="M18.7812 9.25L18.1875 8.625C18.0312 8.46875 17.7812 8.46875 17.6562 8.625L12 14.2812L6.3125 8.625C6.1875 8.46875 5.9375 8.46875 5.78125 8.625L5.1875 9.25C5.03125 9.375 5.03125 9.625 5.1875 9.78125L11.7188 16.3125C11.875 16.4688 12.0938 16.4688 12.25 16.3125L18.7812 9.78125C18.9375 9.625 18.9375 9.375 18.7812 9.25Z"
                                            fill="#333333"
                                        />
                                    </svg>
                                }
                            />
                        </div>
                        <div className="flex relative flex-col gap-5 justify-start self-stretch lg:items-center lg:flex-row">
                            <p className="flex-grow w-full max-w-[196px] text-sm font-bold text-left text-[#373737]">
                                Cantidad
                            </p>

                            <Input
                                // label="Tarifa por hora"
                                placeholder={currencyCode || "GTQ"}
                                onChange={(e) => handleChange(e.target)}
                                value={formData.tariff}
                                name="tariff"
                                type="text"
                                // icon={
                                //     <p className=" text-base text-right text-[#373737] -translate-x-5  ">
                                //         30
                                //     </p>
                                // }
                            />
                        </div>
                        <div className="flex relative justify-start items-center self-stretch">
                            <p className="flex-grow w-full max-w-[212px] text-sm font-bold text-left text-[#373737]">
                                Total adeudado
                            </p>
                            <p className="flex-grow w-full max-w-[212px] text-sm text-right text-[#373737]">
                                {" "}
                                {currencyCode || "GTQ"} 30,00
                            </p>
                        </div>
                        <div className="flex justify-start items-center self-stretch  relative pb-5 border-t-0 border-r-0 border-b border-l-0 border-[#cdcdcd]">
                            <div className="flex justify-start items-center flex-grow relative lg:gap-[5px]">
                                <p className=" text-sm font-bold text-left text-[#373737]">
                                    Cuota de procesamiento
                                </p>
                                <InfoHover text={"Cuota de procesamiento "} />
                            </div>
                            <p className="flex-grow w-full max-w-[212px] text-sm text-right text-[#373737]">
                                {currencyCode || "GTQ"} 0,99
                            </p>
                        </div>
                        <div className="flex relative justify-start items-center self-stretch">
                            <p className="flex-grow w-full max-w-[212px] text-sm font-bold text-left text-[#373737]">
                                Pago adeudado
                            </p>
                            <p className="flex-grow w-full max-w-[212px] text-sm text-right text-[#373737]">
                                {currencyCode || "GTQ"} 30,99
                            </p>
                        </div>
                        <Button
                            onClick={handleFormSubmission}
                            stretch
                            // className="flex justify-center items-center self-stretch text-sm lg:text-lg text-left text-white h-12 relative gap-2.5 px-4 py-1 lg:px-8 lg:py-2.5 rounded bg-[#d80027]"
                        >
                            Confirmar y pagar {currencyCode || "GTQ"} {formData.tariff || null}
                        </Button>
                    </div>
                </div>
            </div>
            <div className="relative flex flex-col items-center justify-start gap-5 lg:gap-1.5 lg:items-start w-full max-w-[936px] ">
                <p className=" text-sm text-left text-[#373737]">
                    Copyright @2023 EPROF
                </p>
                <div className="flex flex-col lg:flex-row justify-start items-center lg:items-start relative gap-3.5">
                    <a
                        href="/platform/funds"
                        className=" text-sm text-left text-[#7b7b7b]"
                    >
                        Política de provacidad{" "}
                    </a>
                    <a
                        href="/platform/funds"
                        className=" text-sm text-left text-[#7b7b7b]"
                    >
                        Términos y condiciones
                    </a>
                    <a
                        href="/platform/funds"
                        className=" text-sm text-left text-[#7b7b7b]"
                    >
                        Política de infracción de derechos de autor
                    </a>
                    <a
                        href="/platform/funds"
                        className=" text-sm text-left text-[#7b7b7b]"
                    >
                        Código de conducta
                    </a>
                </div>
            </div>
        </div>
    );
}

export default Funds;
